import { ProductId, Manifest, TimeStep, SnowStation, CustomActivityConfig, WxStationMetric, StreamflowSiteFilter, StreamflowColorMode } from './types';
import { FoliageService } from './services/FoliageService';
import { ACTIVITY_SETTINGS } from './config';

const defaultFoliageInfo = FoliageService.getDefaultDateInfo();
import ManifestTimelineHelper, { ProductBoundsInfo } from './services/ManifestTimelineHelper';
import { PersonaId } from './personas/types';

export type UiFontSize = 'compact' | 'standard' | 'large';

export interface UserUiLayout {
  drawerPosition: 'bottom' | 'right';
  drawerHeight?: number;
  legendPosition?: { x: number; y: number } | null;
  legendWidth?: number;
  legendHeight?: number;
  isLegendCollapsed?: boolean;
  timelineHorizon?: 5 | 10;
  uiFontSize?: UiFontSize;
  units?: 'imperial' | 'metric';
  updatedAt?: number;
}

export interface AppState {
  activePersona: PersonaId;
  primaryProduct: ProductId | null;
  productAvailability: Record<string, ProductBoundsInfo>;
  cloudsEnabled: boolean;
  windsEnabled: boolean;
  smokeEnabled: boolean;
  dustEnabled: boolean;
  streamflowEnabled: boolean;
  streamflowSiteFilter: StreamflowSiteFilter;
  streamflowColorMode: StreamflowColorMode;
  streamflowMinCfs: number;
  streamflowTempMapEnabled: boolean;
  streamflowLabelsEnabled: boolean;
  wxStationsEnabled: boolean;
  wxStationsOverlayEnabled: boolean;
  wxStationsMetric: WxStationMetric;
  aqiStationLabelsEnabled: boolean;
  streamflowSelectedStation: any | null;
  snowDepthEnabled: boolean;
  snowDepthUnits: 'imperial' | 'metric';
  snowDepthModel: 'snodas' | 'ecmwf';
  snowDepthMinInches: number;
  snowDepthMinElevationFt: number;
  snowDepthShowStations: boolean;
  snowDepthSelectedStation: SnowStation | null;
  snowAccumEnabled: boolean;
  snowAccumUnits: 'imperial' | 'metric';
  snowAccumMode: 'cumulative' | 'daily';
  snowAccumHours: number;
  snowAccumDay: number;
  snowAccumModel: string;
  snowAccumMinThresholdIn: number;
  precipAccumEnabled: boolean;
  precipAccumUnits: 'imperial' | 'metric';
  precipAccumMode: 'cumulative' | 'daily';
  precipAccumHours: number;
  precipAccumDay: number;
  precipAccumModel: string;
  precipAccumMinThresholdIn: number;
  sstEnabled: boolean;
  sstBuoysEnabled: boolean;
  wildfiresEnabled: boolean;
  wildfiresCanadaEnabled: boolean;
  prescribedBurnsEnabled: boolean;
  wildfireHotspotsEnabled: boolean;
  fireRestrictionsEnabled: boolean;
  avalancheEnabled: boolean;
  auroraEnabled: boolean;
  opticsDewRiskEnabled: boolean;
  seeingEnabled: boolean;
  freezingLevelFillEnabled: boolean;
  droughtEnabled: boolean;
  droughtMode: 'usdm' | 'outlook';
  cpcOutlookEnabled: boolean;
  cpcHorizon: '6_10' | '8_14' | 'monthly' | 'three_month';
  cpcVariable: 'temp' | 'precip';
  tropicalEnabled: boolean;
  foliageEnabled: boolean;
  foliageSelectedDate: string;
  foliageMonthIndex: number; // 0 = Sept, 1 = Oct, 2 = Nov
  foliageCanopyOverlay: boolean;
  foliageViewMode: 'forecast' | 'vibrancy' | 'peak_date' | 'canopy' | 'species' | 'heat_stress' | 'soil_moisture';
  foliageSpeciesSubtype: 'maple' | 'aspen' | 'oak' | 'larch';
  foliageSpeciesSelected: Array<'maple' | 'aspen' | 'oak' | 'larch'>;
  foliageModel: string;
  foliageTreedOnly: boolean;
  alpineWildflowersEnabled: boolean;
  alpineWildflowerViewMode: 'peak_date' | 'status' | 'mbii';
  alpineWildflowerDomain: 'na' | 'global';
  alpineWildflowerSelectedDate: string;
  alpineWildflowerMonthIndex: number;
  alpineWildflowerSpecies: string;

  desertWildflowersEnabled: boolean;
  desertWildflowerViewMode: 'peak_date' | 'status' | 'mbii';
  desertWildflowerDomain: 'na' | 'global';
  desertWildflowerSelectedDate: string;
  desertWildflowerMonthIndex: number;
  desertWildflowerSpecies: string;

  wildBerriesEnabled: boolean;
  wildBerryViewMode: 'peak_date' | 'status' | 'mbii';
  wildBerryDomain: 'na' | 'global';
  wildBerrySelectedDate: string;
  wildBerryMonthIndex: number;
  wildBerrySpecies: string;

  // Legacy shared aliases for backwards compatibility
  wildflowerViewMode: 'peak_date' | 'status' | 'mbii';
  wildflowerDomain: 'na' | 'global';
  wildflowerSelectedDate: string;
  wildflowerMonthIndex: number;
  wildflowerSpecies: string;
  mushroomEnabled: boolean;
  mushroomMode: 'current' | 'outlook';
  mushroomSpecies: string;
  mushroomSummary: any | null;
  climateEnabled: boolean;
  climateSelectedDate: string;
  climateMonthIndex: number; // 0 - 11 (Jan = 0)
  climateSelectedWeek: number; // 1 - 52 (Mid March = 11)
  climateViewMode: 'daily' | 'monthly' | 'weekly';
  climateSelectedVariable: string;
  climateSavedTimelineRange: any[] | null;
  recentClimateEnabled: boolean;
  recentClimateYear: number;
  recentClimateTimeMode: 'ytd' | 'month';
  recentClimateMonth: number; // 1 - 12
  recentClimateVariable: 'tmax' | 'tmin' | 'tavg' | 'tanom' | 'prcp' | 'prcpanom' | 'snow';
  auroraAvailableDates: string[];
  noaaKpForecast: Array<{ time_tag: string; kp: number; observed?: string; noaa_scale?: string | null }>;
  milkyWayLineEnabled: boolean;
  milkyWayArEnabled: boolean;
  sunMoonArEnabled: boolean;
  arTargetMode: 'sun' | 'moon';
  arPerspectiveMode: 'first-person' | 'observer';
  arFocalLength: number;
  arSelectedDate: Date | null;
  arSelectedHour: number;
  darkSkiesScore: number | null;
  userTimezone: string;
  selectedModels: Record<string, string>; // product -> modelName
  activeLayerProducts: Record<string, boolean>; // product -> isEnabled
  currentTimeStep: Date | null;
  timelineRange: TimeStep[];
  timelineSunMoonPhaseEnabled: boolean;
  timelineForecastCoverageEnabled: boolean;
  timelineHorizon: 5 | 10;
  drawerPosition: 'bottom' | 'right';
  uiFontSize: UiFontSize;
  isUiLayoutDirty: boolean;
  savedUiLayout: UserUiLayout | null;
  theme: 'neutral' | 'light' | 'dark' | 'road' | 'terrain-mapterhorn' | 'terrain' | 'usgs-naip' | 'usgs-hillshade' | 'usgs-topo' | 'osm' | 'openstreetmap' | 'recent-satellite';
  recentSatelliteProduct: 'modis' | 'hls';
  recentSatelliteMountainMode: 'off' | 'most-recent' | 'landsat-n' | 'landsat-n1' | 'sentinel2-n' | 'sentinel2-n1';
  recentSatelliteDayOffset: number; // For single selection: offset index (0..6), or -1 for 'all'
  recentSatelliteOpacity: number; // 0.0 - 1.0 (default 1.0)
  recentSatelliteHlsSelectedDates: string[]; // Selected HLS date strings for multi-selection / toggle
  recentSatelliteDateOverlay: boolean; // Show capture date text overlay across tiles
  vectorThemeMode: 'dark' | 'light';
  hypsometricTintEnabled: boolean;
  mapCenter: [number, number];
  mapZoom: number;
  terrain3dEnabled: boolean;
  terrainExaggeration: number;
  reliefShadingEnabled: boolean;
  reliefShadingIntensity: number;
  elevationContoursEnabled: boolean;
  panTiltZoomEnabled: boolean;
  savedBasemapOverrideState: {

    theme: string;
    vectorThemeMode: 'dark' | 'light';
    hypsometricTintEnabled: boolean;
    terrain3dEnabled: boolean;
    activeOverrideLayerId: string;
  } | null;
  
  // Tunables
  alpenglowMode: 'alpenglow' | 'goldenhour';
  alpenglow3dSimulation: boolean;
  lenticularMode: 'cap' | 'trapped' | 'socked';
  windDisplayMode: 'average' | 'gusts';
  windDensity: number;
  windSpeed: number;
  windParticlesPlaying: boolean;
  windParticlesVisible: boolean;
  windParticleDensityLevel: 'low' | 'medium' | 'high';
  windColorMode: 'speed' | 'white';
  windParticleSize: 'fine' | 'normal' | 'bold';
  windTrailLength: 'short' | 'medium' | 'long';
  windAnimationThrottle: '30fps' | 'unlimited';
  windColorBatching: 'low' | 'med' | 'high';
  windCanvasResolution: 'low' | 'med' | 'high';
  contourSpacing: 'low' | 'high';
  weatherOpacity: number;
  layerOpacities: Record<string, number>;
  units: 'imperial' | 'metric';

  cloudModel: string;
  cloudModels: string[];
  cloudLevels: string[];
  smokeModel: string;
  smokeLevels: string[];
  aqiMode: 'aqi' | 'aqhi';
  
  // Volcanoes/Lenticular state
  selectedVolcano: string | null;
  lenticularForecast: Record<string, any>;
  showLenticularWinds: boolean;
  showJetStreamWinds: boolean;
  showJetStreamRaster: boolean;
  jetStreamDisplayMode: 'both' | 'raster' | 'particles';
  jetStreamParticlesPlaying: boolean;
  jetStreamParticlesVisible: boolean;
  jetStreamParticleDensityLevel: 'low' | 'medium' | 'high';
  jetStreamDensity: number;
  jetStreamColorMode: 'speed' | 'white';
  jetStreamParticleSize: 'fine' | 'normal' | 'bold';
  jetStreamTrailLength: 'short' | 'medium' | 'long';
  jetStreamSpeed: number;
  
  // Live Weather State
  hrtempMode: 'current' | 'forecast';
  hrtempRasterEnabled: boolean;
  temperatureHoverReadoutEnabled: boolean;
  liveWeatherRadarEnabled: boolean;
  liveWeatherSatelliteEnabled: boolean;
  liveWeatherTempGradientEnabled: boolean;
  liveWeatherTempGradientOpacity: number;
  liveWeatherRadarPlaying: boolean;
  liveWeatherPlaybackSpeed: number; // multiplier (1.0 = 10 fps default, 2.0 = 20 fps)
  liveWeatherRadarPlaybackSpeed: number; // 1.0 = 10 fps, 2.0 = 20 fps, 4.0 = 40 fps
  liveWeatherRadarLoopHours: number; // 1, 2, 4, or 8 hours
  liveWeatherRadarOpacity: number;
  liveWeatherRadarFrameCount: number;
  liveWeatherRadarLightningEnabled: boolean;
  liveWeatherLightningOpacity: number;
  liveWeatherQpeMode: 'off' | '1h' | '24h' | '72h';
  liveWeatherQpeOpacity: number;
  liveWeatherSnowMode: 'off' | '24h' | '48h' | '72h';
  liveWeatherSnowOpacity: number;
  liveWeatherSatelliteOpacity: number;
  liveWeatherSunIntensityEnabled: boolean;
  liveWeatherSunIntensityOpacity: number;
  liveWeatherRtmaWindsEnabled: boolean;
  liveWeatherRtmaWindsOpacity: number;
  liveWeatherRtmaWindsMode: 'average' | 'gusts';
  liveWeatherAqiStationsEnabled: boolean;
  liveWeatherAqiStationsOpacity: number;

  sunIntensityModel: string;
  precipViewMode: 'hourly' | 'prob' | '24h_rain' | '24h_snow' | '24h';
  lightningViewMode: 'daily' | 'hourly';

  liveWeatherSatelliteSource: 'goes_composite' | 'goes_east' | 'goes_west' | 'global_ir' | 'himawari' | 'meteosat' | 'gmgsi';
  liveWeatherSatelliteSources: Array<'goes_composite' | 'goes_east' | 'goes_west' | 'global_ir' | 'himawari' | 'meteosat' | 'gmgsi'>;
  liveWeatherSatRenderMode: 'solid' | 'transparent';
  liveWeatherSatellitePlaying: boolean;
  liveWeatherSatelliteFrameCount: number; // 12, 24, 36
  liveWeatherSatelliteLoopHours: number; // 2, 4, or 8 hours
  liveWeatherSatPlaybackSpeed: number; // 1.0 = 5 fps, 2.0 = 10 fps, 4.0 = 20 fps
  liveWeatherSatelliteDaylightOnly: boolean;

  // Satellite Cloud Deck Discrete Overlays
  liveWeatherSatOverlayHighEnabled: boolean;
  liveWeatherSatOverlayMidEnabled: boolean;
  liveWeatherSatOverlayFogEnabled: boolean;
  liveWeatherSatOverlayHighOpacity: number;
  liveWeatherSatOverlayMidOpacity: number;
  liveWeatherSatOverlayFogOpacity: number;

  // Forecast & Unified Dock Playback State
  liveWeatherObservedBuffering: boolean;
  activeDockPlaybackMode: 'observed' | 'forecast';
  forecastAnimationPlaying: boolean;
  forecastAnimationBuffering: boolean;
  forecastAnimationDurationHours: number; // 12, 24, 48
  forecastAnimationSpeed: number; // 1.0 (1 fps), 2.0 (2 fps), 4.0 (4 fps)
  forecastAnimationStartTime: Date | null;

  // Environment Context
  manifest: Manifest | null;

  mapLayers: {
    forests: boolean;
    roads: boolean;
    grasslands: boolean;
    recreation: boolean;
    paths: boolean;
    waterways: boolean;
    boundaries: boolean;
    public_lands?: boolean;
  };
  selectedPoi: { lat: number; lng: number; elev: number; minElev?: number; maxElev?: number } | null;
  showSunAngles: boolean;
  showMoonAngles: boolean;
  solarLunarHillshadeEnabled: boolean;
  bortleOverlayEnabled: boolean;
  hikingTrailsOverlayEnabled: boolean;
  publicLandsOverlayEnabled: boolean;
  solarLunarToolsEnabled: boolean;
  solarLunarSelectedDate: Date | null;
  solarLunarMonthOffset: number;
  solarLunarBortle: number;
  solarLunarSavedTimelineRange: any[] | null;
  webcamsEnabled: boolean;
  webcamsSavedTimelineRange: any[] | null;
  selectedWebcamDate: string;
  activeLegendOrder: string[];
  lastActivatedProduct: string | null;
  dockedTrendCollapsed: boolean;
  trendRefLinesEnabled: boolean;
  showLegendTrendCharts: boolean;
  legendTrendHorizonDays: 1 | 3 | 5 | 10;

  // Model Comparison Drawer state
  showModelComparisonDrawer: boolean;
  modelComparisonProduct: string;
  modelComparisonMode: 'single_product' | 'elevation' | 'meteogram';
  modelComparisonHorizon: 1 | 3 | 5 | 10;
  modelComparisonExpanded: boolean;
  modelComparisonDisabledModels: string[];
  modelComparisonScale: 'auto' | 'fixed';
  modelComparisonConsensus: boolean;

  // Authentication & Profile state
  user: {
    uid: string;
    email: string | null;
    displayName: string | null;
    photoURL?: string | null;
  } | null;
  isAuthenticated: boolean;
  isAuthLoading: boolean;
  isAuthModalOpen: boolean;
  isProfileModalOpen: boolean;
  sessionToken: string | null;
  subscription: {
    hasAccess: boolean;
    status: 'active' | 'trialing' | 'expired' | 'comped' | 'none';
    plan: string | null;
    currentPeriodEnd: number | null;
    trialEnds: number | null;
    tier?: 'free' | 'registered' | 'pro' | 'ultra';
  };

  // User Activities and Drawer Curation
  enabledActivities: string[];
  customActivities: CustomActivityConfig[];
}

type StateListener = (state: AppState) => void;

const getTodayDateStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const defaults = (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.defaults) || {
  theme: 'usgs-topo',
  vector_theme_mode: 'light',
  map_center: [-121.6972, 45.3736],
  map_zoom: 4,
  weather_opacity: 0.80,
  wind_density: 4500,
  wind_speed: 0.15,
  jet_stream_wind_density: 4500,
  jet_stream_wind_speed: 0.15,
  units: (typeof localStorage !== 'undefined' && ['imperial', 'metric'].includes(localStorage.getItem('skyskout_units') || ''))
    ? (localStorage.getItem('skyskout_units') as 'imperial' | 'metric')
    : ((typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.defaults?.units) || 'imperial'),
  lenticular_mode: 'cap',
  wind_display_mode: 'average',
  cloud_model: 'hrrr',
  cloud_levels: ['low', 'mid', 'high'],
  smoke_model: 'hrrr',
  smoke_levels: ['surface'],
  aqi_mode: 'aqi',
  wind_animation_throttle: (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.defaults?.wind_animation_throttle) || 'unlimited',
  wind_color_batching: (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.defaults?.wind_color_batching) || 'high',
  wind_canvas_resolution: (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.defaults?.wind_canvas_resolution) || 'high',
  contour_spacing: (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.defaults?.contour_spacing) || 'low'
};

const defaultModels = (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.default_models) || {
  sunrise: 'gw_v2_blend_50',
  alpenglow: 'gw_v2_blend_50',
  goldenhour: 'gw_v2_blend_50',
  darkskies: 'blend',
  lenticular: 'ecmwf',
  clouds: 'hrrr',
  winds: 'nbm',
  hrtemp: 'skysk_hres_topo',
  hrtemp_contours: 'skysk_hres_topo',
  hrtemp_current: 'rtma',
  lightning: 'blend',
  cape: 'blend',
  thunderstorm: 'hrrr',
  smoke: 'hrrr',
  aqi: 'hrrr',
  precip: 'nbm',
  precip_prob: 'nbm',
  precip_24h: 'nbm',
  snow_24h: 'nbm',
  foliage: 'seasonal',
  mushrooms: 'flush',
  cloud_inversion: 'nbm',
  cloud_base: 'nbm',
  mountain_visibility: 'nbm',
  seeing: 'rdps',
  optics_dew_risk: 'rdps'
};

const mapLayers = (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.map_layers) || {
  forests: true,
  roads: true,
  grasslands: true,
  recreation: true,
  paths: false,
  waterways: true,
  boundaries: true,
  public_lands: false
};

export interface CachedAuthRecord {
  uid: string;
  user: AppState['user'];
  sessionToken: string | null;
  subscription: AppState['subscription'];
  updatedAt: number;
}

const CACHED_AUTH_STORAGE_KEY = 'skyskout_cached_auth_v1';
const SESSION_TOKEN_STORAGE_KEY = 'skyskout_session_token';

/**
 * Decodes and validates expiration of a Gatekeeper session token:
 * base64url(uid.expiresAtSec.tier.sigHex) or legacy base64url(uid.expiresAtSec.sigHex)
 */
export function parseSessionTokenClaims(token: string | null | undefined): {
  uid: string;
  expiresAtSec: number;
  tier: 'free' | 'registered' | 'pro' | 'ultra';
} | null {
  if (!token || typeof token !== 'string') return null;
  try {
    const b64 = token.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '==='.slice((b64.length + 3) % 4);
    const decoded = typeof atob === 'function' ? atob(padded) : '';
    if (!decoded) return null;
    const parts = decoded.split('.');
    if (parts.length < 3) return null;
    const uid = parts[0];
    const expiresAtSec = parseInt(parts[1], 10);
    const nowSec = Math.floor(Date.now() * 0.001);
    if (!uid || !Number.isFinite(expiresAtSec) || expiresAtSec <= nowSec) {
      return null;
    }
    let tier: 'free' | 'registered' | 'pro' | 'ultra' = 'pro';
    if (parts.length >= 4) {
      const rawTier = parts[2];
      if (rawTier === 'free' || rawTier === 'registered' || rawTier === 'pro' || rawTier === 'ultra') {
        tier = rawTier;
      }
    }
    return { uid, expiresAtSec, tier };
  } catch (_) {
    return null;
  }
}

export function saveCachedAuthState(record: CachedAuthRecord): void {
  try {
    const serialized = JSON.stringify(record);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(CACHED_AUTH_STORAGE_KEY, serialized);
      if (record.sessionToken) {
        localStorage.setItem(SESSION_TOKEN_STORAGE_KEY, record.sessionToken);
      }
    }
  } catch (_) {}
  try {
    const serialized = JSON.stringify(record);
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(CACHED_AUTH_STORAGE_KEY, serialized);
      if (record.sessionToken) {
        sessionStorage.setItem(SESSION_TOKEN_STORAGE_KEY, record.sessionToken);
      }
    }
  } catch (_) {}
}

export function clearCachedAuthState(): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(CACHED_AUTH_STORAGE_KEY);
      localStorage.removeItem(SESSION_TOKEN_STORAGE_KEY);
    }
  } catch (_) {}
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(CACHED_AUTH_STORAGE_KEY);
      sessionStorage.removeItem(SESSION_TOKEN_STORAGE_KEY);
    }
  } catch (_) {}
}

export function loadInitialAuthBootstrap(): {
  user: AppState['user'];
  isAuthenticated: boolean;
  sessionToken: string | null;
  subscription: AppState['subscription'];
} {
  const fallback = {
    user: null,
    isAuthenticated: false,
    sessionToken: null,
    subscription: {
      hasAccess: false,
      status: 'none' as const,
      plan: null,
      currentPeriodEnd: null,
      trialEnds: null,
      tier: 'free' as const
    }
  };

  if (typeof window === 'undefined') return fallback;

  try {
    const rawToken =
      (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(SESSION_TOKEN_STORAGE_KEY)) ||
      (typeof localStorage !== 'undefined' && localStorage.getItem(SESSION_TOKEN_STORAGE_KEY)) ||
      null;
    const rawCached =
      (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(CACHED_AUTH_STORAGE_KEY)) ||
      (typeof localStorage !== 'undefined' && localStorage.getItem(CACHED_AUTH_STORAGE_KEY)) ||
      null;

    let cachedRecord: CachedAuthRecord | null = null;
    if (rawCached) {
      try {
        cachedRecord = JSON.parse(rawCached) as CachedAuthRecord;
      } catch (_) {
        cachedRecord = null;
      }
    }

    const candidateToken = rawToken || cachedRecord?.sessionToken || null;
    const claims = parseSessionTokenClaims(candidateToken);
    if (!claims) {
      return fallback;
    }

    const matchingCached = cachedRecord && cachedRecord.uid === claims.uid ? cachedRecord : null;
    const resolvedTier = matchingCached?.subscription?.tier || claims.tier;
    const hasAccess = Boolean(
      matchingCached?.subscription?.hasAccess ||
      resolvedTier === 'pro' ||
      resolvedTier === 'ultra'
    );

    return {
      user: matchingCached?.user || {
        uid: claims.uid,
        email: null,
        displayName: 'Explorer',
        photoURL: null
      },
      isAuthenticated: true,
      sessionToken: candidateToken,
      subscription: {
        hasAccess,
        status: matchingCached?.subscription?.status || (hasAccess ? 'active' : 'none'),
        plan: matchingCached?.subscription?.plan || null,
        currentPeriodEnd: matchingCached?.subscription?.currentPeriodEnd || null,
        trialEnds: matchingCached?.subscription?.trialEnds || null,
        tier: resolvedTier
      }
    };
  } catch (_) {
    return fallback;
  }
}

const initialAuthBootstrap = loadInitialAuthBootstrap();

class StateStore {
  private state: AppState = {
    activePersona: (typeof localStorage !== 'undefined' && (localStorage.getItem('gw_active_persona') as PersonaId)) || 'standard_forecast',
    primaryProduct: null,
    productAvailability: {},
    cloudsEnabled: false,
    windsEnabled: false,
    smokeEnabled: false,
    dustEnabled: false,
    streamflowEnabled: false,
    streamflowSiteFilter: ((typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.streamflow?.default_site_filter) as StreamflowSiteFilter) || 'both',
    streamflowColorMode: ((typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.streamflow?.default_color_mode) as StreamflowColorMode) || 'percent_normal',
    streamflowMinCfs: 0,
    streamflowTempMapEnabled: false,
    streamflowLabelsEnabled: (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.streamflow?.default_labels_enabled !== undefined)
      ? Boolean(__APP_CONFIG__.streamflow.default_labels_enabled)
      : true,
    wxStationsEnabled: false,
    wxStationsOverlayEnabled: (typeof localStorage !== 'undefined' && localStorage.getItem('skyskout_wx_overlay_enabled') !== null)
      ? localStorage.getItem('skyskout_wx_overlay_enabled') === 'true'
      : ((typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.wx_stations?.overlay_default_enabled !== undefined)
        ? Boolean(__APP_CONFIG__.wx_stations.overlay_default_enabled)
        : true),
    wxStationsMetric: ((typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.wx_stations?.default_metric) as WxStationMetric) || 'temp',
    aqiStationLabelsEnabled: (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.aqi_stations?.default_labels_enabled !== undefined)
      ? Boolean(__APP_CONFIG__.aqi_stations.default_labels_enabled)
      : true,
    streamflowSelectedStation: null,
    snowDepthEnabled: false,
    snowDepthUnits: ((typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.snow_depth?.default_units) as any) || 'imperial',
    snowDepthModel: ((typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.snow_depth?.default_model) as any) || 'snodas',
    snowDepthMinInches: (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.snow_depth?.default_min_depth_in) || 0,
    snowDepthMinElevationFt: (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.snow_depth?.default_min_elevation_ft) || 0,
    snowDepthShowStations: (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.snow_depth?.show_stations_by_default) ?? true,
    snowDepthSelectedStation: null,
    snowAccumEnabled: false,
    snowAccumUnits: ((typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.snow_accum?.default_units) as any) || 'imperial',
    snowAccumMode: ((typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.snow_accum?.default_mode) as any) || 'cumulative',
    snowAccumHours: (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.snow_accum?.default_hours) || 24,
    snowAccumDay: (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.snow_accum?.default_day) || 1,
    snowAccumModel: ((typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.snow_accum?.default_model) as any) || 'ecmwf',
    snowAccumMinThresholdIn: (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.snow_accum?.default_min_threshold_in) || 0.1,
    precipAccumEnabled: false,
    precipAccumUnits: ((typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.precip_accum?.default_units) as any) || 'imperial',
    precipAccumMode: ((typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.precip_accum?.default_mode) as any) || 'cumulative',
    precipAccumHours: (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.precip_accum?.default_hours) || 24,
    precipAccumDay: (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.precip_accum?.default_day) || 1,
    precipAccumModel: ((typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.precip_accum?.default_model) as any) || 'cmorph2',
    precipAccumMinThresholdIn: (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.precip_accum?.default_min_threshold_in) || 0.01,
    sstEnabled: false,
    sstBuoysEnabled: false,
    wildfiresEnabled: false,
    wildfiresCanadaEnabled: true,
    prescribedBurnsEnabled: false,
    wildfireHotspotsEnabled: true,
    fireRestrictionsEnabled: false,
    avalancheEnabled: false,
    droughtEnabled: false,
    droughtMode: 'usdm',
    cpcOutlookEnabled: false,
    cpcHorizon: '6_10',
    cpcVariable: 'temp',
    tropicalEnabled: false,
    auroraEnabled: false,
    opticsDewRiskEnabled: false,
    seeingEnabled: false,
    freezingLevelFillEnabled: false,
    auroraAvailableDates: [],
    noaaKpForecast: [],
    milkyWayLineEnabled: false,
    milkyWayArEnabled: false,
    sunMoonArEnabled: false,
    arTargetMode: 'sun',
    arPerspectiveMode: 'observer',
    arFocalLength: 35,
    arSelectedDate: null,
    arSelectedHour: 18,
    terrain3dEnabled: false,
    terrainExaggeration: (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.ui?.default_terrain_exaggeration) || 1.0,
    reliefShadingEnabled: (() => {
      const initTheme = (defaults.theme === 'terrain-mapterhorn' || defaults.theme === 'terrain' || defaults.theme === 'recent-satellite') ? 'road' : (defaults.theme || 'road');
      const isSkyskoutOsm = !initTheme || initTheme === 'road' || initTheme === 'light' || initTheme === 'dark' || initTheme === 'neutral';
      if (!isSkyskoutOsm) return false;
      return (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.ui?.default_relief_shading_enabled !== undefined)
        ? Boolean(__APP_CONFIG__.ui.default_relief_shading_enabled)
        : true;
    })(),
    reliefShadingIntensity: (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.ui?.default_relief_shading_intensity) || 0.40,
    elevationContoursEnabled: (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.ui?.default_elevation_contours_enabled) || false,
    panTiltZoomEnabled: false,
    darkSkiesScore: null,

    userTimezone: (typeof Intl !== 'undefined' && Intl.DateTimeFormat) ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'America/Los_Angeles',
    selectedModels: {
      ...defaultModels,
      darkskies: (defaultModels.darkskies && defaultModels.darkskies !== 'bortle') ? defaultModels.darkskies : 'blend'
    },
    activeLayerProducts: {},
    currentTimeStep: null,
    timelineRange: [],
    timelineSunMoonPhaseEnabled: true,
    timelineForecastCoverageEnabled: true,
    timelineHorizon: 10,
    drawerPosition: ((typeof localStorage !== 'undefined' && localStorage.getItem('gw_drawer_position') as any) || 'bottom'),
    uiFontSize: ((typeof localStorage !== 'undefined' && localStorage.getItem('gw_ui_font_size') as UiFontSize) || 'compact'),
    isUiLayoutDirty: false,
    savedUiLayout: null,
    theme: (defaults.theme === 'terrain-mapterhorn' || defaults.theme === 'terrain' || defaults.theme === 'recent-satellite') ? 'road' : (defaults.theme || 'road'),
    recentSatelliteProduct: 'modis',
    recentSatelliteMountainMode: 'off',
    recentSatelliteDayOffset: 0,
    recentSatelliteOpacity: 1.0,
    recentSatelliteHlsSelectedDates: [],
    recentSatelliteDateOverlay: false,
    vectorThemeMode: (defaults as any).vector_theme_mode || 'light',
    hypsometricTintEnabled: (defaults as any).hypsometric_tint_enabled ?? false,
    mapCenter: defaults.map_center,
    mapZoom: defaults.map_zoom,
    savedBasemapOverrideState: null,
    alpenglowMode: 'goldenhour',
    alpenglow3dSimulation: false,
    lenticularMode: defaults.lenticular_mode,
    windDisplayMode: defaults.wind_display_mode,
    windDensity: defaults.wind_density,
    windSpeed: defaults.wind_speed,
    windParticlesPlaying: true,
    windParticlesVisible: true,
    windParticleDensityLevel: 'medium',
    windColorMode: 'speed',
    windParticleSize: 'normal',
    windTrailLength: 'medium',
    windAnimationThrottle: (typeof window !== 'undefined' && ['30fps', 'unlimited'].includes(localStorage.getItem('skyskout_wind_throttle') || ''))
      ? (localStorage.getItem('skyskout_wind_throttle') as '30fps' | 'unlimited')
      : defaults.wind_animation_throttle,
    windColorBatching: (typeof window !== 'undefined' && ['low', 'med', 'high'].includes(localStorage.getItem('skyskout_wind_color_batching') || ''))
      ? (localStorage.getItem('skyskout_wind_color_batching') as 'low' | 'med' | 'high')
      : defaults.wind_color_batching,
    windCanvasResolution: (typeof window !== 'undefined' && ['low', 'med', 'high'].includes(localStorage.getItem('skyskout_wind_canvas_res') || ''))
      ? (localStorage.getItem('skyskout_wind_canvas_res') as 'low' | 'med' | 'high')
      : defaults.wind_canvas_resolution,
    contourSpacing: (typeof window !== 'undefined' && ['low', 'high'].includes(localStorage.getItem('skyskout_contour_spacing') || ''))
      ? (localStorage.getItem('skyskout_contour_spacing') as 'low' | 'high')
      : defaults.contour_spacing,
    weatherOpacity: defaults.weather_opacity,
    layerOpacities: {},
    units: defaults.units,

    cloudModel: defaults.cloud_model,
    cloudModels: [defaults.cloud_model || 'ecmwf'],
    cloudLevels: [...defaults.cloud_levels],
    smokeModel: defaults.smoke_model,
    smokeLevels: [...defaults.smoke_levels],
    aqiMode: defaults.aqi_mode,
    selectedVolcano: null,
    lenticularForecast: {},
    showLenticularWinds: false,
    showJetStreamWinds: true,
    showJetStreamRaster: true,
    jetStreamDisplayMode: 'both',
    jetStreamParticlesPlaying: true,
    jetStreamParticlesVisible: true,
    jetStreamParticleDensityLevel: 'medium',
    jetStreamDensity: (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.defaults?.jet_stream_wind_density) || 4500,
    jetStreamColorMode: 'speed',
    jetStreamParticleSize: 'normal',
    jetStreamTrailLength: 'medium',
    jetStreamSpeed: (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.defaults?.jet_stream_wind_speed) || 0.15,
    hrtempMode: 'forecast',
    hrtempRasterEnabled: true,
    temperatureHoverReadoutEnabled: (typeof localStorage !== 'undefined' && localStorage.getItem('skyskout_temp_hover_enabled') !== null)
      ? localStorage.getItem('skyskout_temp_hover_enabled') === 'true'
      : true,
    liveWeatherRadarEnabled: false,
    liveWeatherSatelliteEnabled: false,
    liveWeatherTempGradientEnabled: false,
    liveWeatherTempGradientOpacity: 0.70,
    liveWeatherRadarPlaying: false,
    liveWeatherPlaybackSpeed: 1.0,
    liveWeatherRadarPlaybackSpeed: (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.live_weather?.radar_playback_speed) || 1.0,
    liveWeatherRadarLoopHours: (() => {
      const opts: number[] = (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.live_weather?.observed_duration_options) || [2, 4, 8];
      const raw = (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.live_weather?.radar_loop_hours) || 8;
      return opts.includes(raw) ? raw : (opts[opts.length - 1] || 8);
    })(),
    liveWeatherRadarOpacity: 0.70,
    liveWeatherRadarFrameCount: 0,
    liveWeatherRadarLightningEnabled: false,
    liveWeatherLightningOpacity: (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.live_weather?.lightning_opacity) ?? 1.0,
    liveWeatherQpeMode: 'off',
    liveWeatherQpeOpacity: 0.75,
    liveWeatherSnowMode: 'off',
    liveWeatherSnowOpacity: 0.85,
    liveWeatherSatelliteOpacity: 0.70,
    liveWeatherSunIntensityEnabled: false,
    liveWeatherSunIntensityOpacity: 0.70,
    liveWeatherRtmaWindsEnabled: false,
    liveWeatherRtmaWindsOpacity: 0.80,
    liveWeatherRtmaWindsMode: 'average',
    liveWeatherAqiStationsEnabled: false,
    liveWeatherAqiStationsOpacity: 0.85,
    sunIntensityModel: 'rrfs',
    precipViewMode: 'hourly',
    lightningViewMode: 'daily',

    liveWeatherSatelliteSource: ((typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.live_weather?.satellite_source) as any) || 'goes_composite',
    liveWeatherSatelliteSources: ((typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.live_weather?.satellite_sources) as any) || ['goes_composite', 'himawari', 'meteosat'],
    liveWeatherSatRenderMode: 'transparent',
    liveWeatherSatellitePlaying: false,
    liveWeatherSatelliteFrameCount: 12,
    liveWeatherSatelliteLoopHours: (() => {
      const opts: number[] = (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.live_weather?.observed_duration_options) || [2, 4, 8];
      const raw = (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.live_weather?.satellite_loop_hours) || 8;
      return opts.includes(raw) ? raw : (opts[opts.length - 1] || 8);
    })(),
    liveWeatherSatPlaybackSpeed: (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.live_weather?.satellite_playback_speed) || 1.0,
    liveWeatherSatelliteDaylightOnly: true,

    // Satellite Cloud Deck Discrete Overlays
    liveWeatherSatOverlayHighEnabled: false,
    liveWeatherSatOverlayMidEnabled: false,
    liveWeatherSatOverlayFogEnabled: false,
    liveWeatherSatOverlayHighOpacity: 0.85,
    liveWeatherSatOverlayMidOpacity: 0.85,
    liveWeatherSatOverlayFogOpacity: 0.85,

    // Forecast & Unified Dock Playback State
    liveWeatherObservedBuffering: false,
    activeDockPlaybackMode: 'forecast',
    forecastAnimationPlaying: false,
    forecastAnimationBuffering: false,
    forecastAnimationDurationHours: (() => {
      const opts: number[] = (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.forecast_animation?.duration_options) || [12, 24, 48];
      const raw = (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.forecast_animation?.default_duration_hours) || 12;
      return opts.includes(raw) ? raw : (opts[0] || 12);
    })(),
    forecastAnimationSpeed: (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.forecast_animation?.default_playback_speed) || 2.0,
    forecastAnimationStartTime: null,

    foliageEnabled: false,
    foliageSelectedDate: defaultFoliageInfo.dateStr,
    foliageMonthIndex: defaultFoliageInfo.monthIndex,
    foliageCanopyOverlay: false,
    foliageViewMode: 'peak_date',
    foliageSpeciesSubtype: 'maple',
    foliageSpeciesSelected: ['maple', 'aspen', 'oak', 'larch'],
    foliageModel: 'seasonal',
    foliageTreedOnly: defaults.foliage_treed_only || false,
    alpineWildflowersEnabled: false,
    alpineWildflowerViewMode: 'peak_date',
    alpineWildflowerDomain: 'na',
    alpineWildflowerSelectedDate: '2026-07-15',
    alpineWildflowerMonthIndex: 4,
    alpineWildflowerSpecies: 'composite',

    desertWildflowersEnabled: false,
    desertWildflowerViewMode: 'peak_date',
    desertWildflowerDomain: 'na',
    desertWildflowerSelectedDate: '2026-03-15',
    desertWildflowerMonthIndex: 2,
    desertWildflowerSpecies: 'composite',

    wildBerriesEnabled: false,
    wildBerryViewMode: 'peak_date',
    wildBerryDomain: 'na',
    wildBerrySelectedDate: '2026-08-15',
    wildBerryMonthIndex: 2,
    wildBerrySpecies: 'composite',

    // Legacy shared defaults
    wildflowerViewMode: 'peak_date',
    wildflowerDomain: 'na',
    wildflowerSelectedDate: '2026-07-15',
    wildflowerMonthIndex: 2,
    wildflowerSpecies: 'composite',
    mushroomEnabled: false,
    mushroomMode: 'current',
    mushroomSpecies: 'general_flush',
    mushroomSummary: null,
    climateEnabled: false,
    climateSelectedDate: getTodayDateStr(),
    climateMonthIndex: new Date().getMonth(),
    climateSelectedWeek: 11,
    climateViewMode: 'daily',
    climateSelectedVariable: 'tmax_mean',
    climateSavedTimelineRange: null,
    recentClimateEnabled: false,
    recentClimateYear: new Date().getFullYear(),
    recentClimateTimeMode: ((typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.ui?.recent_climate?.default_time_mode) as any) || 'ytd',
    recentClimateMonth: new Date().getMonth() + 1,
    recentClimateVariable: ((typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.ui?.recent_climate?.default_variable) as any) || 'tanom',

    manifest: null,
    mapLayers: { ...mapLayers },
    selectedPoi: null,
    showSunAngles: false,
    showMoonAngles: false,
    solarLunarHillshadeEnabled: false,
    bortleOverlayEnabled: false,
    hikingTrailsOverlayEnabled: false,
    publicLandsOverlayEnabled: false,
    solarLunarToolsEnabled: false,
    solarLunarSelectedDate: null,
    solarLunarMonthOffset: 0,
    solarLunarBortle: 4,
    solarLunarSavedTimelineRange: null,
    webcamsEnabled: false,
    webcamsSavedTimelineRange: null,
    selectedWebcamDate: getTodayDateStr(),
    activeLegendOrder: [],
    lastActivatedProduct: null,
    dockedTrendCollapsed: typeof window !== 'undefined' && localStorage.getItem('skyskout_docked_trend_collapsed') === 'true',
    trendRefLinesEnabled: typeof window !== 'undefined' ? localStorage.getItem('skyskout_trend_ref_lines') !== 'false' : true,
    showLegendTrendCharts: typeof window !== 'undefined' && localStorage.getItem('skyskout_show_legend_charts') !== null
      ? localStorage.getItem('skyskout_show_legend_charts') === 'true'
      : true,
    legendTrendHorizonDays: (typeof window !== 'undefined' && [1, 3, 5, 10].includes(Number(localStorage.getItem('skyskout_legend_horizon')))
      ? (Number(localStorage.getItem('skyskout_legend_horizon')) as 1 | 3 | 5 | 10)
      : 5),

    showModelComparisonDrawer: false,
    modelComparisonProduct: 'hrtemp',
    modelComparisonMode: 'single_product',
    modelComparisonHorizon: (typeof window !== 'undefined' && [1, 3, 5, 10].includes(Number(localStorage.getItem('skyskout_comparison_horizon')))
      ? (Number(localStorage.getItem('skyskout_comparison_horizon')) as 1 | 3 | 5 | 10)
      : 3),
    modelComparisonExpanded: false,
    modelComparisonDisabledModels: [],
    modelComparisonScale: (typeof window !== 'undefined' && localStorage.getItem('skyskout_comparison_scale') === 'fixed')
      ? 'fixed'
      : 'auto',
    modelComparisonConsensus: (typeof window !== 'undefined' && localStorage.getItem('skyskout_comparison_consensus') === 'true'),

    user: initialAuthBootstrap.user,
    isAuthenticated: initialAuthBootstrap.isAuthenticated,
    isAuthLoading: true,
    isAuthModalOpen: false,
    isProfileModalOpen: false,
    sessionToken: initialAuthBootstrap.sessionToken,
    subscription: initialAuthBootstrap.subscription,

    enabledActivities: (() => {
      try {
        const saved = localStorage.getItem('gw_enabled_activities');
        if (saved) {
          const list = JSON.parse(saved);
          if (Array.isArray(list)) {
            if (!list.includes('standard_forecast')) {
              list.unshift('standard_forecast');
            }
            return list;
          }
        }
      } catch (_) {}
      return ACTIVITY_SETTINGS?.default_enabled_activities || ['standard_forecast', 'hiking', 'photography', 'stargazing', 'winter_sports', 'water_sports', 'advanced'];
    })(),
    customActivities: (() => {
      try {
        const saved = localStorage.getItem('gw_custom_activities');
        if (saved) return JSON.parse(saved);
      } catch (_) {}
      return [];
    })()
  };

  private listeners: Set<StateListener> = new Set();

  public getState(): AppState {
    return { ...this.state };
  }

  public update(update: Partial<AppState>): void {
    if (update.theme === 'terrain-mapterhorn' || (update.theme as any) === 'terrain' || update.theme === 'recent-satellite') {
      update.theme = 'road';
    }
    if (update.theme !== undefined && update.theme !== this.state.theme && update.reliefShadingEnabled === undefined) {
      const isSkyskoutOsm = !update.theme || update.theme === 'road' || update.theme === 'light' || update.theme === 'dark' || update.theme === 'neutral';
      update.reliefShadingEnabled = isSkyskoutOsm
        ? ((typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.ui?.default_relief_shading_enabled !== undefined)
            ? Boolean(__APP_CONFIG__.ui.default_relief_shading_enabled)
            : true)
        : false;
    }
    if (update.temperatureHoverReadoutEnabled !== undefined && typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem('skyskout_temp_hover_enabled', String(update.temperatureHoverReadoutEnabled));
      } catch {}
    }
    if (update.windAnimationThrottle !== undefined && typeof localStorage !== 'undefined') {
      try { localStorage.setItem('skyskout_wind_throttle', update.windAnimationThrottle); } catch {}
    }
    if (update.windColorBatching !== undefined && typeof localStorage !== 'undefined') {
      try { localStorage.setItem('skyskout_wind_color_batching', update.windColorBatching); } catch {}
    }
    if (update.windCanvasResolution !== undefined && typeof localStorage !== 'undefined') {
      try { localStorage.setItem('skyskout_wind_canvas_res', update.windCanvasResolution); } catch {}
    }
    if (update.contourSpacing !== undefined && typeof localStorage !== 'undefined') {
      try { localStorage.setItem('skyskout_contour_spacing', update.contourSpacing); } catch {}
    }
    const prev = { ...this.state };
    this.state = { ...this.state, ...update };
    
    // Check if anything actually changed
    let changed = false;
    for (const key of Object.keys(update) as Array<keyof AppState>) {
      if (prev[key] !== this.state[key]) {
        changed = true;
        break;
      }
    }
    
    if (changed) {
      this.notify();
    }
  }

  public subscribe(listener: StateListener): () => void {
    this.listeners.add(listener);
    // Initial notify
    listener(this.getState());
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify(): void {
    const currentState = this.getState();
    this.listeners.forEach(listener => {
      try {
        listener(currentState);
      } catch (err) {
        console.error('[StateStore] Error in listener:', err);
      }
    });
  }
}

export const store = new StateStore();
export default store;

export function getActiveSessionToken(): string | null {
  try {
    if (store && typeof store.getState === 'function') {
      const fromState = store.getState().sessionToken;
      if (fromState && parseSessionTokenClaims(fromState)) return fromState;
    }
  } catch (_) {}
  try {
    const fromSession = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(SESSION_TOKEN_STORAGE_KEY) : null;
    if (fromSession && parseSessionTokenClaims(fromSession)) return fromSession;
    const fromLocal = typeof localStorage !== 'undefined' ? localStorage.getItem(SESSION_TOKEN_STORAGE_KEY) : null;
    if (fromLocal && parseSessionTokenClaims(fromLocal)) return fromLocal;
  } catch (_) {}
  return null;
}

export function getLayerOpacity(productId: string, defaultVal: number = 1.0): number {
  const state = store.getState();
  const indOp = state.layerOpacities[productId] !== undefined ? state.layerOpacities[productId] : defaultVal;
  return indOp * state.weatherOpacity;
}

export function clearSingleLayer(layerKey: string): void {
  const updates: any = {};
  const currentActive = { ...(store.getState().activeLayerProducts || {}) };

  // Delete key from activeLayerProducts map
  delete currentActive[layerKey];

  if (layerKey === 'sunrise' || layerKey === 'golden_hour') {
    if (store.getState().primaryProduct === 'sunrise') updates.primaryProduct = null;
    delete currentActive['sunrise'];
    delete currentActive['golden_hour'];
  } else if (layerKey === 'alpenglow' || layerKey === 'alpenglow_forecast') {
    if (store.getState().primaryProduct === 'alpenglow') updates.primaryProduct = null;
    delete currentActive['alpenglow'];
    delete currentActive['alpenglow_forecast'];
  } else if (layerKey === 'darkskies' || layerKey === 'darkskies_bortle') {
    if (store.getState().primaryProduct === 'darkskies') updates.primaryProduct = null;
    updates.milkyWayLineEnabled = false;
    updates.milkyWayArEnabled = false;
    updates.darkSkiesScore = null;
    delete currentActive['darkskies'];
    delete currentActive['darkskies_bortle'];
  } else if (layerKey === 'optics_dew_risk') {
    if (store.getState().primaryProduct === 'optics_dew_risk') updates.primaryProduct = null;
    updates.opticsDewRiskEnabled = false;
    delete currentActive['optics_dew_risk'];
  } else if (layerKey === 'seeing') {
    if (store.getState().primaryProduct === 'seeing') updates.primaryProduct = null;
    updates.seeingEnabled = false;
    delete currentActive['seeing'];
  } else if (layerKey === 'lenticular' || layerKey === 'lenticular_clouds') {
    if (store.getState().primaryProduct === 'lenticular') updates.primaryProduct = null;
    delete currentActive['lenticular'];
    delete currentActive['lenticular_clouds'];
  } else if (layerKey === 'clouds') {
    updates.cloudsEnabled = false;
    if (store.getState().primaryProduct === 'clouds') updates.primaryProduct = null;
    delete currentActive['clouds'];
  } else if (layerKey === 'winds' || layerKey === 'winds_surface') {
    updates.windsEnabled = false;
    if (store.getState().primaryProduct === 'winds') updates.primaryProduct = null;
    delete currentActive['winds'];
    delete currentActive['winds_surface'];
  } else if (layerKey === 'winds_surface_current' || layerKey === 'rtma_winds') {
    updates.liveWeatherRtmaWindsEnabled = false;
    delete currentActive['winds_surface_current'];
    delete currentActive['rtma_winds'];
  } else if (layerKey === 'hrtemp') {
    if (store.getState().primaryProduct === 'hrtemp') updates.primaryProduct = null;
    delete currentActive['hrtemp'];
    delete currentActive['hrtemp_contours'];
  } else if (layerKey === 'hrtemp_current' || layerKey === 'live_weather_temp_gradient' || layerKey === 'live_weather_temps') {
    updates.liveWeatherTempGradientEnabled = false;
    if (store.getState().primaryProduct === 'hrtemp_current') updates.primaryProduct = null;
    delete currentActive['hrtemp_current'];
    delete currentActive['live_weather_temp_gradient'];
    delete currentActive['live_weather_temps'];
  } else if (layerKey === 'hrtemp_contours') {
    if (store.getState().primaryProduct === 'hrtemp_contours') updates.primaryProduct = null;
    delete currentActive['hrtemp_contours'];
  } else if (layerKey === 'lightning' || layerKey === 'lightning_forecast') {
    if (store.getState().primaryProduct === 'lightning') updates.primaryProduct = null;
    delete currentActive['lightning'];
    delete currentActive['lightning_forecast'];
  } else if (layerKey === 'live_weather_radar' || layerKey === 'precip_radar') {
    updates.liveWeatherRadarEnabled = false;
    delete currentActive['live_weather_radar'];
    delete currentActive['precip_radar'];
  } else if (layerKey === 'live_weather_lightning' || layerKey === 'lightning_live') {
    updates.liveWeatherRadarLightningEnabled = false;
    delete currentActive['live_weather_lightning'];
    delete currentActive['lightning_live'];
  } else if (layerKey === 'live_weather_satellite' || layerKey === 'satellite_imagery' || layerKey.includes('satellite')) {
    updates.liveWeatherSatelliteEnabled = false;
    updates.liveWeatherSatOverlayHighEnabled = false;
    updates.liveWeatherSatOverlayMidEnabled = false;
    updates.liveWeatherSatOverlayFogEnabled = false;
    delete currentActive['live_weather_satellite'];
    delete currentActive['satellite_imagery'];
  } else if (layerKey === 'live_weather_qpe' || layerKey === 'rain_accum' || layerKey === 'precip_accum') {
    updates.liveWeatherQpeMode = 'off';
    updates.precipAccumEnabled = false;
    delete currentActive['live_weather_qpe'];
    delete currentActive['rain_accum'];
    delete currentActive['precip_accum'];
  } else if (layerKey === 'snow_accum') {
    updates.snowAccumEnabled = false;
    delete currentActive['snow_accum'];
  } else if (layerKey === 'smoke' || layerKey === 'smoke_forecast') {
    updates.smokeEnabled = false;
    if (store.getState().primaryProduct === 'smoke') updates.primaryProduct = null;
    delete currentActive['smoke'];
    delete currentActive['smoke_forecast'];
  } else if (layerKey === 'dust' || layerKey === 'dust_forecast') {
    updates.dustEnabled = false;
    if (store.getState().primaryProduct === 'dust') updates.primaryProduct = null;
    delete currentActive['dust'];
    delete currentActive['dust_forecast'];
  } else if (layerKey === 'aqi' || layerKey === 'aqi_index') {
    if (store.getState().primaryProduct === 'aqi') updates.primaryProduct = null;
    delete currentActive['aqi'];
    delete currentActive['aqi_index'];
  } else if (layerKey === 'wx_stations') {
    updates.wxStationsEnabled = false;
    if (store.getState().primaryProduct === 'wx_stations') updates.primaryProduct = null;
    delete currentActive['wx_stations'];
  } else if (layerKey === 'aqi_stations') {
    updates.liveWeatherAqiStationsEnabled = false;
    delete currentActive['aqi_stations'];
  } else if (layerKey === 'precip' || layerKey === 'precip_forecast') {
    if (store.getState().primaryProduct === 'precip') updates.primaryProduct = null;
    delete currentActive['precip'];
    delete currentActive['precip_forecast'];
  } else if (layerKey === 'valley_fog') {
    if (store.getState().primaryProduct === 'valley_fog') updates.primaryProduct = null;
    delete currentActive['valley_fog'];
  } else if (layerKey === 'cloud_inversion') {
    if (store.getState().primaryProduct === 'cloud_inversion') updates.primaryProduct = null;
    delete currentActive['cloud_inversion'];
  } else if (layerKey === 'cloud_base') {
    if (store.getState().primaryProduct === 'cloud_base') updates.primaryProduct = null;
    delete currentActive['cloud_base'];
  } else if (layerKey === 'mountain_visibility') {
    if (store.getState().primaryProduct === 'mountain_visibility') updates.primaryProduct = null;
    delete currentActive['mountain_visibility'];
  } else if (layerKey === 'windchill') {
    if (store.getState().primaryProduct === 'windchill') updates.primaryProduct = null;
    delete currentActive['windchill'];
  } else if (layerKey === 'relative_humidity') {
    if (store.getState().primaryProduct === 'relative_humidity') updates.primaryProduct = null;
    delete currentActive['relative_humidity'];
  } else if (layerKey === 'dewpoint') {
    if (store.getState().primaryProduct === 'dewpoint') updates.primaryProduct = null;
    delete currentActive['dewpoint'];
  } else if (layerKey === 'cape' || layerKey === 'thunderstorm') {
    if (store.getState().primaryProduct === 'cape' || store.getState().primaryProduct === 'thunderstorm') updates.primaryProduct = null;
    delete currentActive['cape'];
    delete currentActive['thunderstorm'];
  } else if (layerKey === 'freezing_level' || layerKey === 'freezing_level_contours') {
    if (store.getState().primaryProduct === 'freezing_level' || store.getState().primaryProduct === 'freezing_level_contours') {
      updates.primaryProduct = null;
    }
    delete currentActive['freezing_level'];
    delete currentActive['freezing_level_contours'];
  } else if (layerKey === 'sst') {
    updates.sstEnabled = false;
    if (store.getState().primaryProduct === 'sst') updates.primaryProduct = null;
    delete currentActive['sst'];
  } else if (layerKey === 'snow_depth') {
    updates.snowDepthEnabled = false;
    if (store.getState().primaryProduct === 'snow_depth') updates.primaryProduct = null;
    delete currentActive['snow_depth'];
  } else if (layerKey === 'snow_accum') {
    updates.snowAccumEnabled = false;
    if (store.getState().primaryProduct === 'snow_accum') updates.primaryProduct = null;
    delete currentActive['snow_accum'];
  } else if (layerKey === 'precip_accum') {
    updates.precipAccumEnabled = false;
    if (store.getState().primaryProduct === 'precip_accum') updates.primaryProduct = null;
    delete currentActive['precip_accum'];
  } else if (layerKey === 'streamflow' || layerKey === 'streamflow_gauges' || layerKey === 'water_temp') {
    updates.streamflowEnabled = false;
    if (store.getState().primaryProduct === 'streamflow') updates.primaryProduct = null;
    delete currentActive['streamflow'];
    delete currentActive['streamflow_gauges'];
    delete currentActive['water_temp'];
  } else if (layerKey === 'sun_intensity' || layerKey === 'sun_intensity_actual' || layerKey === 'sun_intensity_forecast') {
    updates.liveWeatherSunIntensityEnabled = false;
    if (store.getState().primaryProduct === 'sun_intensity') updates.primaryProduct = null;
    delete currentActive['sun_intensity'];
    delete currentActive['sun_intensity_actual'];
    delete currentActive['sun_intensity_forecast'];
  } else if (layerKey === 'aurora' || layerKey === 'aurora_viewline' || layerKey === 'kp_index') {
    updates.auroraEnabled = false;
    if (store.getState().primaryProduct === 'aurora') updates.primaryProduct = null;
    delete currentActive['aurora'];
    delete currentActive['aurora_viewline'];
    delete currentActive['kp_index'];
  } else if (layerKey === 'bortle') {
    updates.bortleOverlayEnabled = false;
    delete currentActive['bortle'];
  } else if (layerKey === 'foliage') {
    if (store.getState().primaryProduct === 'foliage') updates.primaryProduct = null;
    delete currentActive['foliage'];
  } else if (layerKey === 'alpine_wildflowers') {
    updates.alpineWildflowersEnabled = false;
    if (store.getState().primaryProduct === 'alpine_wildflowers') updates.primaryProduct = null;
    delete currentActive['alpine_wildflowers'];
  } else if (layerKey === 'desert_wildflowers') {
    updates.desertWildflowersEnabled = false;
    if (store.getState().primaryProduct === 'desert_wildflowers') updates.primaryProduct = null;
    delete currentActive['desert_wildflowers'];
  } else if (layerKey === 'wild_berries') {
    updates.wildBerriesEnabled = false;
    if (store.getState().primaryProduct === 'wild_berries') updates.primaryProduct = null;
    delete currentActive['wild_berries'];
  } else if (layerKey === 'mushrooms') {
    if (store.getState().primaryProduct === 'mushrooms' || store.getState().primaryProduct === 'mushrooms_outlook') {
      updates.primaryProduct = null;
    }
    delete currentActive['mushrooms'];
    delete currentActive['mushrooms_outlook'];
  } else if (layerKey === 'wildfires' || layerKey === 'wildfire_hotspots') {
    updates.wildfiresEnabled = false;
    if (store.getState().primaryProduct === 'wildfires') updates.primaryProduct = null;
    delete currentActive['wildfires'];
    delete currentActive['wildfire_hotspots'];
  } else if (layerKey === 'fire_restrictions') {
    updates.fireRestrictionsEnabled = false;
    if (store.getState().primaryProduct === 'fire_restrictions') updates.primaryProduct = null;
    delete currentActive['fire_restrictions'];
  } else if (layerKey === 'avalanche') {
    updates.avalancheEnabled = false;
    if (store.getState().primaryProduct === 'avalanche') updates.primaryProduct = null;
    delete currentActive['avalanche'];
  } else if (layerKey === 'solar_lunar_tools' || layerKey === 'solar_lunar') {
    updates.solarLunarToolsEnabled = false;
    updates.showSunAngles = false;
    updates.showMoonAngles = false;
    updates.milkyWayLineEnabled = false;
    updates.milkyWayArEnabled = false;
    updates.bortleOverlayEnabled = false;
    updates.solarLunarHillshadeEnabled = false;
    updates.sunMoonArEnabled = false;
    delete currentActive['solar_lunar_tools'];
    delete currentActive['solar_lunar'];
    delete currentActive['bortle'];
  } else if (layerKey === 'turbulence' || layerKey === 'aviation_turbulence') {
    if (store.getState().primaryProduct === 'turbulence' || store.getState().primaryProduct === 'aviation_turbulence') updates.primaryProduct = null;
    delete currentActive['turbulence'];
    delete currentActive['aviation_turbulence'];
  } else if (layerKey === 'jet_stream') {
    if (store.getState().primaryProduct === 'jet_stream') updates.primaryProduct = null;
    delete currentActive['jet_stream'];
  } else if (layerKey === 'recent_climate' || layerKey === 'climatedata') {
    updates.recentClimateEnabled = false;
    if (store.getState().primaryProduct === 'recent_climate') updates.primaryProduct = null;
    delete currentActive['recent_climate'];
    delete currentActive['climatedata'];
  } else {
    if (store.getState().primaryProduct === layerKey) updates.primaryProduct = null;
    delete currentActive[layerKey];
  }

  const currentOrder = store.getState().activeLegendOrder || [];
  updates.activeLegendOrder = currentOrder.filter(k => {
    if (layerKey === 'turbulence' || layerKey === 'aviation_turbulence') {
      return k !== 'turbulence' && k !== 'aviation_turbulence';
    }
    return k !== layerKey;
  });
  updates.activeLayerProducts = currentActive;

  const curState = store.getState();
  const nextRadar = updates.liveWeatherRadarEnabled !== undefined ? updates.liveWeatherRadarEnabled : curState.liveWeatherRadarEnabled;
  const nextLightning = updates.liveWeatherRadarLightningEnabled !== undefined ? updates.liveWeatherRadarLightningEnabled : curState.liveWeatherRadarLightningEnabled;
  const nextSat = updates.liveWeatherSatelliteEnabled !== undefined ? updates.liveWeatherSatelliteEnabled : curState.liveWeatherSatelliteEnabled;
  if (!nextRadar && !nextLightning && !nextSat) {
    updates.liveWeatherRadarPlaying = false;
    updates.liveWeatherSatellitePlaying = false;
    updates.liveWeatherObservedBuffering = false;
    const lw = typeof window !== 'undefined' ? (window as any).liveWeatherLayer : null;
    if (lw && typeof lw.stopObservedAnimation === 'function') {
      lw.stopObservedAnimation(false);
    }
  }

  store.update(updates);
}

export function clearAllLayers(): void {
  const lw = typeof window !== 'undefined' ? (window as any).liveWeatherLayer : null;
  if (lw && typeof lw.stopObservedAnimation === 'function') {
    lw.stopObservedAnimation(false);
  }
  store.update({
    primaryProduct: null,
    cloudsEnabled: false,
    windsEnabled: false,
    smokeEnabled: false,
    dustEnabled: false,
    streamflowEnabled: false,
    wildfiresEnabled: false,
    fireRestrictionsEnabled: false,
    avalancheEnabled: false,
    webcamsEnabled: false,
    webcamsSavedTimelineRange: null,
    liveWeatherRadarEnabled: false,
    liveWeatherSatelliteEnabled: false,
    liveWeatherRadarPlaying: false,
    liveWeatherSatellitePlaying: false,
    liveWeatherObservedBuffering: false,
    forecastAnimationPlaying: false,
    forecastAnimationBuffering: false,
    wxStationsEnabled: false,
    liveWeatherTempGradientEnabled: false,
    liveWeatherSunIntensityEnabled: false,
    liveWeatherRtmaWindsEnabled: false,
    liveWeatherAqiStationsEnabled: false,
    liveWeatherRadarLightningEnabled: false,
    liveWeatherQpeMode: 'off',
    liveWeatherSnowMode: 'off',
    auroraEnabled: false,
    milkyWayLineEnabled: false,
    milkyWayArEnabled: false,
    sunMoonArEnabled: false,
    showMoonAngles: false,
    showSunAngles: false,
    bortleOverlayEnabled: false,
    hikingTrailsOverlayEnabled: false,
    publicLandsOverlayEnabled: false,
    solarLunarToolsEnabled: false,
    sstEnabled: false,
    opticsDewRiskEnabled: false,
    climateEnabled: false,
    recentClimateEnabled: false,
    activeLayerProducts: {},
    activeLegendOrder: []
  });
}

/**
 * Unifies model updates for a product across selectedModels and legacy state fields,
 * ensuring map layers, trend charts, and model comparison drawers stay in lockstep.
 */
export function setProductModel(product: string, model: string, activateOnMap: boolean = false): void {
  const currentState = store.getState();
  const mLower = model.toLowerCase();

  let prodKey = product;
  if (prodKey === 'sun_intensity_forecast' || prodKey === 'sun_intensity_actual') {
    prodKey = 'sun_intensity';
  }
  if (prodKey === 'aviation_turbulence') {
    prodKey = 'turbulence';
  }

  let effectiveModel = mLower;
  if (prodKey === 'darkskies' && (effectiveModel === 'bortle' || !effectiveModel)) {
    effectiveModel = 'blend';
  }

  const updatedModels = {
    ...currentState.selectedModels,
    [prodKey]: effectiveModel
  };

  // Synchronize twin products
  if (prodKey === 'alpenglow') {
    updatedModels['goldenhour'] = mLower;
  } else if (prodKey === 'goldenhour') {
    updatedModels['alpenglow'] = mLower;
  } else if (prodKey === 'turbulence') {
    updatedModels['aviation_turbulence'] = mLower;
  }

  // Synchronize sun_intensity aliases
  if (prodKey === 'sun_intensity') {
    updatedModels['sun_intensity_forecast'] = mLower;
  }

  // Synchronize precipitation sub-products
  if (prodKey === 'precip') {
    const pMode = currentState.precipViewMode || 'hourly';
    if (pMode === 'prob') {
      updatedModels['precip_prob'] = mLower;
    } else if (pMode === '24h_snow') {
      updatedModels['snow_24h'] = mLower;
    } else if (pMode === '24h' || pMode === '24h_rain') {
      updatedModels['precip_24h'] = mLower;
    }
  } else if (prodKey === 'precip_prob') {
    updatedModels['precip'] = mLower;
  } else if (prodKey === 'snow_24h' || prodKey === 'precip_24h') {
    updatedModels['precip'] = mLower;
  }

  const updates: any = { selectedModels: updatedModels };

  // If requested or if no primary product is active on the map, activate this product so selecting a model renders it
  if (activateOnMap || !currentState.primaryProduct) {
    let targetPrimary = prodKey;
    if (targetPrimary === 'precip_prob' || targetPrimary === 'precip_24h' || targetPrimary === 'snow_24h') {
      targetPrimary = 'precip';
    } else if (targetPrimary === 'aviation_turbulence') {
      targetPrimary = 'turbulence';
    }
    updates.primaryProduct = targetPrimary;
    updates.lastActivatedProduct = targetPrimary;
    updates.activeDockPlaybackMode = 'forecast';
    if (prodKey === 'clouds') updates.cloudsEnabled = true;
    if (prodKey === 'winds') updates.windsEnabled = true;
    if (prodKey === 'smoke') updates.smokeEnabled = true;
    if (prodKey === 'dust') updates.dustEnabled = true;
  }

  // Synchronize companion / legacy individual model fields
  if (prodKey === 'clouds') {
    updates.cloudModel = mLower;
    updates.cloudModels = [mLower];
  } else if (prodKey === 'smoke') {
    updates.smokeModel = mLower;
  } else if (prodKey === 'sun_intensity') {
    updates.sunIntensityModel = mLower;
  } else if (prodKey === 'aqi') {
    updates.aqiMode = mLower === 'raqdps' ? 'aqhi' : 'aqi';
  }

  // When switching sunrise/goldenhour/alpenglow to gw_nowcast, remember the previous NWP model
  // and automatically advance the timeline slider to the next upcoming twilight event.
  if (prodKey === 'sunrise' || prodKey === 'alpenglow' || prodKey === 'goldenhour') {
    const prevModel = String(currentState.selectedModels?.[prodKey] || '').toLowerCase();
    if (prevModel && prevModel !== 'gw_nowcast' && mLower === 'gw_nowcast') {
      try {
        localStorage.setItem(`gw_last_nwp_model_${prodKey}`, prevModel);
      } catch (_) {}
    } else if (mLower && mLower !== 'gw_nowcast') {
      try {
        localStorage.setItem(`gw_last_nwp_model_${prodKey}`, mLower);
      } catch (_) {}
    }

    if (mLower === 'gw_nowcast') {
      const nextEventDate = ManifestTimelineHelper.getNextNowcastEventDate(
        currentState.manifest,
        prodKey,
        currentState.timelineRange
      );
      if (nextEventDate) {
        updates.currentTimeStep = nextEventDate;
      }
    }
  }

  store.update(updates);
}

export function setTrendRefLinesEnabled(enabled: boolean): void {
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.setItem('skyskout_trend_ref_lines', String(enabled));
    } catch (_) {}
  }
  store.update({ trendRefLinesEnabled: enabled });
}
